import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Modal, ModalBody, ModalContent, ModalOverlay, Text } from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useUser } from "../../contexts/UserContext";
import { isConsentTarget, isImpersonating } from "../../lib/policyConsentRules";
import { isNativePlatform } from "../../lib/platformUtils";
import { setRolloutGateOpen, useRolloutGateOpen } from "../../lib/rolloutGate";
import { pickGateAnnouncement } from "../../lib/announcementGate";
import AnnouncementBody from "./AnnouncementBody";
import { tt } from "../../i18n/tt";

// ============================================================================
// 타깃 공지 전면 안내 (2026-10-28 대표 지시 — 「크리에이터가 로그인하면 무조건 확인할 수 있도록」)
//
// 노출: UserLayout 에 마운트. 크리에이터(isConsentTarget) · 대리 접속 아님 · 내가 받은 공지 중 announcement.gate = true 이고
//   아직 「확인했습니다」(announcement_recipient.confirmed_at)를 안 누른 가장 최근 공지 1건. 마이그레이션 20261206100000.
// 닫힘: 「확인했습니다」만(ESC·바깥 클릭·안드로이드 뒤로가기 없음). 확인 = confirmed_at + read_at 기록(서버 · 기기 무관).
//   기록에 실패하면 이번 방문에서만 닫는다 — 안내가 앱을 가두면 안 된다(TierRolloutInterstitial 과 같은 원칙). 다음 진입에 다시 뜬다.
// 겹침: 다른 전면 안내(등급제 안내 등)가 떠 있으면 그게 닫힌 뒤에 뜬다(rolloutGate 「한 화면에 안내 하나」).
// 다시 보기: 확인한 뒤에도 「공지·소식 › 나에게 온 공지」에 같은 본문이 남는다.
// ============================================================================

export default function AnnouncementGate() {
  const { userInfo } = useUser();
  const email = userInfo?.email;
  const target = Boolean(email) && isConsentTarget(userInfo?.role) && !isImpersonating();
  const [ann, setAnn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closedId, setClosedId] = useState(null);

  const load = useCallback(async () => {
    if (!target) { setAnn(null); return; }
    try {
      const { data: recips, error: rErr } = await supabase
        .from("announcement_recipient")
        .select("announcement_id, confirmed_at")
        .eq("user_email", email)
        .is("confirmed_at", null);
      if (rErr || !recips?.length) { setAnn(null); return; }
      const { data: anns, error: aErr } = await supabase
        .from("announcement")
        .select("id, title, summary, body, body_html, published_at, gate, status")
        .in("id", recips.map((r) => r.announcement_id))
        .eq("gate", true)
        .eq("status", "published");
      if (aErr) { setAnn(null); return; }
      setAnn(pickGateAnnouncement(anns, recips));
    } catch (err) {
      console.warn("[AnnouncementGate] 조회 실패:", err?.message || err);
      setAnn(null);
    }
  }, [email, target]);

  useEffect(() => { load(); }, [load]);

  // 다른 전면 안내가 덮고 있으면 기다린다 — 내가 연 상태는 mineRef 로 구분한다
  const mineRef = useRef(false);
  const gateOpen = useRolloutGateOpen();
  const otherOpen = gateOpen && !mineRef.current;
  const showing = Boolean(ann) && ann.id !== closedId && !otherOpen;
  useEffect(() => {
    if (showing) { mineRef.current = true; setRolloutGateOpen(true); }
    else if (mineRef.current) { mineRef.current = false; setRolloutGateOpen(false); }
  }, [showing]);
  useEffect(() => () => { if (mineRef.current) { mineRef.current = false; setRolloutGateOpen(false); } }, []);

  // 안드로이드 뒤로가기로 닫히지 않게(확인 버튼으로만)
  useEffect(() => {
    if (!showing || !isNativePlatform()) return undefined;
    let handle;
    import("@capacitor/app").then(({ App }) => {
      handle = App.addListener("backButton", () => {});
    }).catch(() => {});
    return () => { if (handle) handle.then ? handle.then((h) => h.remove()) : handle.remove(); };
  }, [showing]);

  if (!showing) return null;

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const now = new Date().toISOString();
    try {
      const { error } = await supabase
        .from("announcement_recipient")
        .update({ confirmed_at: now })
        .eq("announcement_id", ann.id)
        .eq("user_email", email)
        .is("confirmed_at", null);
      if (error) throw error;
      await supabase
        .from("announcement_recipient")
        .update({ read_at: now })
        .eq("announcement_id", ann.id)
        .eq("user_email", email)
        .is("read_at", null);
    } catch (err) {
      console.warn("[AnnouncementGate] 확인 기록 실패:", err?.message || err);
    }
    setBusy(false);
    setClosedId(ann.id);
    load();   // 확인할 전면 공지가 더 있으면 이어서 뜬다
  };

  return (
    <Modal isOpen onClose={() => {}} size="full" closeOnEsc={false} closeOnOverlayClick={false} autoFocus trapFocus motionPreset="none">
      <ModalOverlay bg="rgba(20,25,23,0.6)" />
      <ModalContent bg="neutral.50" m={0} borderRadius={0} data-testid="announcement-gate">
        <ModalBody p={0} display="flex" flexDirection="column" minH="100dvh">
          <Box flex={1} overflowY="auto" px={{ base: 4, md: 8 }} pt={{ base: 6, md: 10 }} pb={6}>
            <Box maxW="820px" mx="auto" bg="white" border="1px solid" borderColor="neutral.200" borderRadius="8px" boxShadow="sm" p={{ base: 4, md: 7 }}>
              <Text fontSize="xs" fontWeight="700" color="brand.600" letterSpacing="0.04em">{tt("annGate.eyebrow", "레이블리 공지 · 확인이 필요해요")}</Text>
              <Text as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="800" color="neutral.900" mt={1} mb={4} lineHeight="1.4">{ann.title}</Text>
              <AnnouncementBody html={ann.body_html} text={ann.body || ann.summary} fontSize="15px" />
            </Box>
          </Box>
          <Box position="sticky" bottom={0} bg="white" borderTop="1px solid" borderColor="neutral.200"
            px={{ base: 4, md: 8 }} pt={3} pb="calc(12px + env(safe-area-inset-bottom, 0px))">
            <Flex maxW="820px" mx="auto" align="center" gap={3} wrap="wrap">
              <Text fontSize="sm" color="neutral.600" flex="1 1 240px">
                {tt("annGate.hint", "확인한 뒤에도 「공지·소식 › 나에게 온 공지」에서 언제든 다시 볼 수 있어요.")}
              </Text>
              <Button colorScheme="brand" size="lg" onClick={confirm} isLoading={busy} data-testid="announcement-gate-confirm">
                {tt("annGate.confirm", "확인했습니다")}
              </Button>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
